import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listVoices, type Voice } from "../api";
import { useT } from "../i18n";

/**
 * VoicePicker chooses the voice a book is narrated in, and lets you hear each
 * one before you do.
 *
 * The sample is the whole point. Thirty names like Enceladus and Zubenelgenubi
 * carry no information about what they sound like, and the one-word descriptors
 * beside them ("Breathy", "Gravelly") are Google's, not ours — a hint for
 * narrowing thirty down to three, never evidence. So every voice plays the same
 * pre-recorded English sentence, and the decision is made with ears.
 *
 * Pre-recorded and checked in, not synthesised on demand: auditioning is
 * something you do ten times in a minute, and a provider call behind each press
 * would put a bill on the act of choosing (`make voices`).
 *
 * **The voices are the model's, not the application's** (ADR-0109). The flat
 * `voices` array is the union across models and says nothing about which model
 * can speak which — two vendors may each have a `ryan` — so this reads the
 * chosen model's own list, and plays the `sample_url` that comes with each
 * voice rather than rebuilding a path from its name. That path is per model
 * now, which is exactly the bug the field exists to prevent.
 *
 * A model with **one** voice is a choice of model, not of voice: it says which
 * voice it speaks in and offers no list and no play control, because there is
 * no clip to play (ADR-0109 §6).
 *
 * Changing the voice does not re-narrate anything. It is what the *next* render
 * uses — which is why the warning about re-recording lives on the Narrate
 * control, next to the money, rather than here.
 */
export default function VoicePicker({
  value,
  model = "",
  onChange,
  disabled = false,
}: {
  /** The book's chosen voice, or "" for the installation default. */
  value: string;
  /** The book's speech model, or "" for the installation default. */
  model?: string;
  onChange: (voice: string) => void;
  disabled?: boolean;
}) {
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    // Constants. Refetching them is pure cost.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const [open, setOpen] = useState(false);
  const t = useT();
  const [playing, setPlaying] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  // One element for every sample, for the same reason the player uses one for
  // a whole chapter: browsers cap concurrent media, and thirty of them on a
  // phone is thirty decoders for one sentence.
  useEffect(() => {
    const el = new Audio();
    el.addEventListener("ended", () => setPlaying(null));
    audio.current = el;
    return () => {
      el.pause();
      audio.current = null;
    };
  }, []);

  const activeModel = model || catalogue.data?.default_model || "";
  const info = catalogue.data?.models?.find((m) => m.name === activeModel);
  const voices = info?.voices ?? [];
  // The model's default, not the installation's: "the default" means the voice
  // this book would actually be narrated in, and each model declares its own.
  const selected = value || info?.default_voice || catalogue.data?.default || "";
  const current = voices.find((v) => v.name === selected);
  const shown = (v: Voice) => v.label || v.name;

  // One voice is not a choice. Said rather than listed, so nobody hunts for a
  // control that would do nothing.
  if (voices.length === 1) {
    return (
      <div data-testid="voice-picker" className="mt-3">
        <p
          data-testid="voice-fixed"
          className="rounded-control border border-line px-3 py-2 text-sm"
        >
          <span className="text-muted">{t.voice.label}</span>
          <span className="font-medium">{shown(voices[0]!)}</span>
          <span className="block text-xs text-muted">{t.voice.fixedVoice}</span>
        </p>
      </div>
    );
  }

  function audition(voice: Voice) {
    const el = audio.current;
    if (!el || !voice.sample_url) return;
    // A second press on the voice already playing stops it. Without this, the
    // only way to stop a sample is to start another one.
    if (playing === voice.name) {
      el.pause();
      setPlaying(null);
      return;
    }
    el.pause();
    el.src = voice.sample_url;
    setPlaying(voice.name);
    void el.play().catch(() => setPlaying(null));
  }

  return (
    <div data-testid="voice-picker" className="mt-3">
      <button
        type="button"
        data-testid="voice-toggle"
        aria-expanded={open}
        aria-controls="voice-list"
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="flex w-full items-center justify-between gap-2 rounded-control border border-line px-3 py-2 text-start text-sm transition-colors hover:bg-sunk disabled:opacity-40"
      >
        <span>
          <span className="text-muted">{t.voice.label}</span>
          <span data-testid="voice-current" className="font-medium">
            {current ? shown(current) : value ? t.voice.designed : (selected ?? "…")}
          </span>
          {current?.descriptor && <span className="text-muted"> · {current.descriptor}</span>}
          {/* "The default" is a different statement from the name of the
              default, and a book that never chose a voice should say the
              former. */}
          {!value && <span className="text-muted">{t.voice.theDefault}</span>}
        </span>
        <span aria-hidden="true" className="text-muted">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && (
        <div id="voice-list" className="mt-2 rounded-card border border-line bg-surface p-2">
          {/* The sentence is always English (narration.SampleSentence) and this
              line is Persian half the time. Unicode resolves the quotes around
              a Latin run from the paragraph, not from the run, so without a
              <bdi> the closing quote and the full stop walk to the other end of
              the line — which is exactly what the shot showed. */}
          <p className="px-1 pb-2 text-xs text-muted" data-testid="voice-sentence">
            {t.voice.everySample} <bdi>“{catalogue.data?.sentence ?? ""}”</bdi>
          </p>
          {catalogue.isError && (
            <p role="alert" className="px-1 pb-2 text-xs text-red-600">
              {t.voice.loadFailed}
            </p>
          )}
          {/* A scrolling list rather than thirty rows pushing the Narrate
              button off a phone screen. */}
          <ul className="max-h-72 space-y-1 overflow-y-auto" role="listbox">
            {voices.map((voice) => {
              const chosen = voice.name === selected;
              return (
                <li key={voice.name}>
                  <div
                    className={`flex items-center gap-2 rounded-control px-2 py-1.5 ${
                      chosen ? "bg-sage/15" : ""
                    }`}
                  >
                    {/* Play is its own control, deliberately. Auditioning and
                        choosing are different decisions, and a list where
                        hearing a voice also selected it would make trying the
                        thirtieth one an accident.

                        No clip, no button: a control that 404s is worse than
                        none (ADR-0109 §6). The row keeps its width so the
                        names stay in one column. */}
                    {voice.sample_url ? (
                      <button
                        type="button"
                        aria-label={t.voice.playSample(shown(voice))}
                        data-testid={`voice-sample-${voice.name}`}
                        onClick={() => audition(voice)}
                        className="grid size-7 shrink-0 place-items-center rounded-full border border-line text-xs transition-colors hover:bg-sunk"
                      >
                        <span aria-hidden="true">{playing === voice.name ? "◼" : "▶"}</span>
                      </button>
                    ) : (
                      <span aria-hidden="true" className="size-7 shrink-0" />
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={chosen}
                      data-testid={`voice-choose-${voice.name}`}
                      onClick={() => {
                        onChange(voice.name);
                        setOpen(false);
                      }}
                      className="flex min-w-0 flex-1 items-baseline gap-2 text-start text-sm"
                    >
                      <span className={chosen ? "font-medium" : ""}>{shown(voice)}</span>
                      <span className="truncate text-xs text-muted">{voice.descriptor}</span>
                      {chosen && (
                        <span aria-hidden="true" className="text-sage-deep">
                          ✓
                        </span>
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
